// Lightweight client analytics — fires events to /api/analytics/event
// Used by the in-app dashboard at /dashboard. Best-effort; never throws.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND_URL}/api/analytics/event`;
const STORAGE_KEY = "yup_session_id";

function ensureSessionId() {
  try {
    let sid = window.sessionStorage.getItem(STORAGE_KEY);
    if (!sid) {
      sid =
        (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
        `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem(STORAGE_KEY, sid);
    }
    return sid;
  } catch {
    return `s_${Date.now()}`;
  }
}

export function track(event, opts = {}) {
  try {
    const body = {
      session_id: ensureSessionId(),
      event,
      page: opts.page || window.location.pathname || "/",
      value: opts.value ?? null,
      referrer: document.referrer || null,
    };
    // Fire-and-forget — use keepalive so events still send on navigation away
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never throw from analytics */
  }
}

// Page-view helper
export function trackPageView(page) {
  track("page_view", { page: page || window.location.pathname });
}

// Scroll-depth tracker — fires page-scoped milestones at 25/50/75/100%.
// Each milestone fires at most once per page-load.
export function initScrollDepth(pageName) {
  const page = pageName || window.location.pathname;
  const fired = new Set();
  const milestones = [25, 50, 75, 100];

  const handler = () => {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop || 0;
    const winH = window.innerHeight || doc.clientHeight || 1;
    const docH = Math.max(
      doc.scrollHeight,
      doc.offsetHeight,
      document.body?.scrollHeight || 0
    );
    const denom = Math.max(1, docH - winH);
    const pct = Math.min(100, Math.round((scrollTop / denom) * 100));

    milestones.forEach((m) => {
      if (pct >= m && !fired.has(m)) {
        fired.add(m);
        track("scroll_depth", { page, value: m });
      }
    });
  };

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      handler();
      ticking = false;
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  // Trigger once in case the page is short enough to already be 100% visible
  setTimeout(handler, 600);

  return () => window.removeEventListener("scroll", onScroll);
}
